import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/ui-kit";
import { Download, ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { toast } from "sonner";

function monthLabel(y, m) {
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export default function Billing() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/billing/summary", { params: { year, month } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const shift = (delta) => {
    let y = year, m = month + delta;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setYear(y); setMonth(m);
  };

  const totals = useMemo(() => {
    const t = { total_rides: 0, completed: 0, canceled: 0, pending: 0, billable_miles: 0, fare_total: 0 };
    (data?.rows || []).forEach((r) => {
      t.total_rides += r.total_rides; t.completed += r.completed; t.canceled += r.canceled;
      t.pending += r.pending; t.billable_miles += r.billable_miles; t.fare_total += r.fare_total;
    });
    return t;
  }, [data]);

  const download = async () => {
    const token = localStorage.getItem("nemt_token");
    const res = await fetch(`${API_BASE}/billing/summary.csv?year=${year}&month=${month}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `billing_${year}_${String(month).padStart(2, "0")}.csv`;
    a.click();
  };

  const downloadInvoice = async (row) => {
    if (!row.company_id) { toast.error("Agency-direct rides can't be invoiced. Assign a company first."); return; }
    const token = localStorage.getItem("nemt_token");
    const res = await fetch(`${API_BASE}/billing/invoice.pdf?year=${year}&month=${month}&company_id=${row.company_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { toast.error("Failed to generate invoice"); return; }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `invoice_${row.company_name.replace(/\s+/g, "_")}_${year}_${String(month).padStart(2, "0")}.pdf`;
    a.click();
  };

  return (
    <div data-testid="billing-page">
      <PageHeader
        subtitle="Reporting"
        title="Billing Summary"
        action={
          <button onClick={download} className="btn-primary flex items-center gap-1.5" data-testid="download-csv-btn">
            <Download size={14} /> Export CSV
          </button>
        }
      />

      <div className="flex items-center justify-between mb-4">
        <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2 py-1">
          <button onClick={() => shift(-1)} className="p-1.5 hover:bg-slate-50 rounded" data-testid="prev-month"><ChevronLeft size={14} /></button>
          <div className="font-display font-semibold text-sm min-w-[140px] text-center" data-testid="current-month">{monthLabel(year, month)}</div>
          <button onClick={() => shift(1)} className="p-1.5 hover:bg-slate-50 rounded" data-testid="next-month"><ChevronRight size={14} /></button>
        </div>
        <div className="text-xs text-slate-500 font-mono">{data?.period_start} → {data?.period_end}</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        {[
          ["Total rides", totals.total_rides],
          ["Completed", totals.completed],
          ["Pending", totals.pending],
          ["Canceled", totals.canceled],
          ["Billable miles", totals.billable_miles.toFixed(1)],
          ["Fare total", `$${totals.fare_total.toFixed(2)}`],
        ].map(([label, value]) => (
          <div key={label} className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</div>
            <div className="font-display text-xl font-bold text-[#0A192F] mt-1">{value}</div>
          </div>
        ))}
      </div>

      <TableShell
        columns={["Company", "Total", "Completed", "Pending", "Canceled", "Miles", "Fare", "Invoice"]}
        empty={!loading && (!data?.rows || data.rows.length === 0) ? "No rides in this month." : null}
      >
        {(data?.rows || []).map((r) => (
          <tr key={r.company_id || "direct"} className="tbl-row border-t border-slate-100" data-testid={`billing-row-${r.company_id || "direct"}`}>
            <td className="px-4 py-2.5 font-medium">{r.company_name}</td>
            <td className="px-4 py-2.5 font-mono">{r.total_rides}</td>
            <td className="px-4 py-2.5 text-emerald-700 font-mono">{r.completed}</td>
            <td className="px-4 py-2.5 text-amber-700 font-mono">{r.pending}</td>
            <td className="px-4 py-2.5 text-rose-700 font-mono">{r.canceled}</td>
            <td className="px-4 py-2.5 font-mono">{r.billable_miles.toFixed(1)}</td>
            <td className="px-4 py-2.5 font-mono">${r.fare_total.toFixed(2)}</td>
            <td className="px-4 py-2.5">
              {r.company_id ? (
                <button onClick={() => downloadInvoice(r)} className="text-[#005E82] hover:underline text-xs inline-flex items-center gap-1" data-testid={`invoice-pdf-${r.company_id}`}>
                  <FileText size={12} /> PDF
                </button>
              ) : (
                <span className="text-xs text-slate-400">—</span>
              )}
            </td>
          </tr>
        ))}
      </TableShell>

      <div className="mt-4 text-xs text-slate-500">
        Tip: set <span className="font-mono">Billable miles</span> and <span className="font-mono">Fare</span> on each ride to
        populate this report. Fields are available in the ride editor.
      </div>
    </div>
  );
}
